import { Injectable } from "@angular/core";
import { Action, NgxsOnInit, Selector, State, StateContext, createSelector } from "@ngxs/store";
import { tap } from "rxjs/operators";
import { StateResetAll } from "ngxs-reset-plugin";
import { VERSION } from "@version";
import { SystemProvider } from "@/common/providers/system.provider";
import { WebsocketProvider } from "@/common/providers/websocket.provider";
import { AuthProvider } from "@/common/providers/auth.provider";
import { MessageService } from "@/common/services/message.service";
import { PagesProvider } from "@/common/providers/pages.provider";
import { USER_AUTH_STATES, USER_ROLES, VALID_USER_AUTH_STATES } from "@shared/user.constants";
import { Page } from "@shared/page.constants";
import { MENU_GROUPS, MenuGroup, MenuItem } from "@shared/menu.constants";

export class LoginAction {
  static readonly type = "[System] Login";
  constructor(public email: string, public password: string) {}
}

export class LogoutAction {
  static readonly type = "[System] Logout";
}

export class LoginTwoFactorAction {
  static readonly type = "[System] LoginTwoFactor";
  constructor(public token: string, public code: string) {}
}

export class VerifyAction {
  static readonly type = "[System] Verify";
}

export class SetLoginStateAction {
  static readonly type = "[System] SetLoginState";
  constructor(public state: USER_AUTH_STATES, public data?: any) {}
}

export class ResetLoginStateAction {
  static readonly type = "[System] ResetLoginState";
}

export class UpdateUserSettingsAction {
  static readonly type = "[System] UpdateUserSettings";
  constructor(public user: { name?: string }) {}
}

export class WebsocketConnectAction {
  static readonly type = "[System] WebsocketConnect";
}

export class WebsocketDisconnectAction {
  static readonly type = "[System] WebsocketDisconnect";
}

export class GetServerSettingsAction {
  static readonly type = "[System] GetServerSettings";
}

export class GetPageAction {
  static readonly type = "[System] GetPageAction";
  constructor(public slug: string) {}
}

export class AddPageAction {
  static readonly type = "[System] AddPageAction";
  constructor(public page: Page) {}
}

export class RemovePageAction {
  static readonly type = "[System] RemovePageAction";
  constructor(public slug: string) {}
}

export interface SystemStateModel {
  user: any;
  connected: boolean;
  state: USER_AUTH_STATES;
  stateData: any;
  clientVersion: string;
  serverVersion: string;
  settings: any;
  pages: any;
  menus: MenuGroup[];
}

@State<SystemStateModel>({
  name: "system",
  defaults: {
    user: null,
    connected: false,
    state: USER_AUTH_STATES.unauthenticated,
    stateData: null,
    clientVersion: VERSION,
    serverVersion: "",
    settings: {},
    pages: {},
    menus: [],
  },
})
@Injectable()
export class SystemStore implements NgxsOnInit {
  constructor(
    private systemProvider: SystemProvider,
    private authProvider: AuthProvider,
    private websocketProvider: WebsocketProvider,
    private messageService: MessageService,
    private pagesProvider: PagesProvider,
  ) {}

  @Selector()
  static versions(state: SystemStateModel): { clientVersion: string; serverVersion: string } {
    return {
      clientVersion: state.clientVersion,
      serverVersion: state.serverVersion,
    };
  }

  @Selector()
  static isAuthenticated(state: SystemStateModel): boolean {
    return state.user && state.state === USER_AUTH_STATES.authenticated;
  }

  @Selector()
  static user(state: SystemStateModel): any {
    return state.user;
  }

  @Selector()
  static state(state: SystemStateModel): any {
    return state.state;
  }

  @Selector()
  static stateData(state: SystemStateModel): any {
    return state.stateData;
  }

  @Selector()
  static connected(state: SystemStateModel): boolean {
    return state.connected;
  }

  @Selector()
  static isAdmin(state: SystemStateModel): boolean {
    return state.user && state.user.role && state.user.role === USER_ROLES.Admin;
  }

  @Selector()
  static userRole(state: SystemStateModel): USER_ROLES | null {
    return state.user && state.user.role ? state.user.role : null;
  }

  @Selector()
  static userId(state: SystemStateModel): string | null {
    return state.user && state.user._id ? state.user._id : null;
  }

  @Selector()
  static settings(state: SystemStateModel): any {
    return state.settings;
  }

  @Selector()
  static menus(state: SystemStateModel): MenuGroup[] {
    return state.menus;
  }

  static page(slug: string) {
    return createSelector([SystemStore], (state: SystemStateModel) => {
      if (!slug) return null;
      return state.pages[slug];
    });
  }

  static menu(group: MENU_GROUPS) {
    return createSelector([SystemStore], (state: SystemStateModel): MenuItem[] => {
      const menus = state.menus.filter((m) => m.group === group);
      if (menus.length > 0) {
        return menus[0].items;
      }
      return [];
    });
  }

  @Action(GetServerSettingsAction)
  getServerSettingsAction(ctx: StateContext<SystemStateModel>) {
    return this.systemProvider.settings().pipe(
      tap((response) => {
        if (response && response.body) {
          ctx.patchState({
            settings: response.body,
            serverVersion: response.body.version,
            menus: response.body.menus || [],
          });
        }
      })
    );
  }

  @Action(LoginAction)
  login(ctx: StateContext<SystemStateModel>, action: LoginAction) {
    return this.authProvider.login(action.email, action.password).pipe(
      tap((response) => {
        this._validateLoginResponse(ctx, USER_AUTH_STATES.unauthenticated, response);
      })
    );
  }

  @Action(LoginTwoFactorAction)
  loginTwoFactor(ctx: StateContext<SystemStateModel>, action: LoginTwoFactorAction)  {
    return this.authProvider.validateTwoFactorCode(action.token, action.code).pipe(
      tap((response) => {
        this._validateLoginResponse(ctx, USER_AUTH_STATES.twoFactorAuth, response);
      }),
    );
  }

  @Action(VerifyAction)
  verify(ctx: StateContext<SystemStateModel>) {
    return this.authProvider.verify().pipe(
      tap((response) => {
        if (response.success) {
          this._setUser(ctx, response.body);
        }
      })
    );
  }

  @Action(LogoutAction)
  logout(ctx: StateContext<SystemStateModel>) {
    return this.authProvider.logout().pipe(
      tap(() => {
        const connected = ctx.getState().connected;
        this.websocketProvider.emit("auth/logout");
        ctx.dispatch(new StateResetAll(SystemStore)).subscribe(() => {
          ctx.patchState({
            user: null,
            connected,
            state: USER_AUTH_STATES.unauthenticated,
            stateData: null,
          });
        });
        ctx.patchState({
          user: null,
          connected,
          state: USER_AUTH_STATES.unauthenticated,
          stateData: null,
        });
      })
    );
  }

  @Action(SetLoginStateAction)
  setLoginStateAction(ctx: StateContext<SystemStateModel>, action: SetLoginStateAction) {
    if (VALID_USER_AUTH_STATES.includes(action.state)) {
      return ctx.patchState({
        state: action.state,
        stateData: action.data || null,
      });
    }
    return ctx.patchState({
      state: USER_AUTH_STATES.unauthenticated,
      stateData: null,
    });
  }

  @Action(ResetLoginStateAction)
  resetLoginStateAction(ctx: StateContext<SystemStateModel>) {
    return ctx.patchState({
      state: USER_AUTH_STATES.unauthenticated,
      stateData: null,
    });
  }

  @Action(UpdateUserSettingsAction)
  updateUserSettingsAction(ctx: StateContext<SystemStateModel>, action: UpdateUserSettingsAction) {
    const user = ctx.getState().user;
    ctx.patchState({
      user: Object.assign({}, user, {
        name: action.user.name,
      }),
    });
  }

  @Action(WebsocketConnectAction)
  websocketConnect(ctx: StateContext<SystemStateModel>) {
    const user = ctx.getState().user;
    this.websocketProvider.emit("auth/login", user);
    ctx.patchState({
      connected: true,
    });
  }

  @Action(WebsocketDisconnectAction)
  websocketDisconnect(ctx: StateContext<SystemStateModel>) {
    ctx.patchState({
      connected: false,
    });
  }

  @Action(GetPageAction)
  getPage(ctx: StateContext<SystemStateModel>, action: GetPageAction) {
    const state = ctx.getState();
    if (!action.slug || state.pages[action.slug]) {
      return;
    }

    return this.pagesProvider.getBySlug(action.slug).pipe(
      tap((response) => {
        if (!response || !response.success || !response.body) {
          return;
        }

        const pages = Object.assign({}, ctx.getState().pages);
        pages[action.slug] = response.body;
        ctx.patchState({
          pages,
        });
      })
    );
  }

  @Action(AddPageAction)
  addPage(ctx: StateContext<SystemStateModel>, action: AddPageAction) {
    if (!action.page) {
      return;
    }

    const state = ctx.getState();
    if (state.pages[action.page.slug]) {
      return;
    }

    const pages = Object.assign({}, ctx.getState().pages);
    pages[action.page.slug] = action.page;
    ctx.patchState({
      pages,
    });
  }

  @Action(RemovePageAction)
  removePage(ctx: StateContext<SystemStateModel>, action: RemovePageAction) {
    const state = ctx.getState();
    if (!state.pages[action.slug]) {
      return;
    }

    const pages = Object.assign({}, ctx.getState().pages);
    delete pages[action.slug];
    ctx.patchState({
      pages,
    });
  }

  ngxsOnInit(ctx?: StateContext<SystemStateModel>) {
    ctx?.dispatch(new GetServerSettingsAction());
  }

  private _validateLoginResponse(ctx: StateContext<SystemStateModel>, state: USER_AUTH_STATES, response: any): void {
    // Successful Login
    if (response.success && response.body && !response.body.state && response.body._id) {
      this._setUser(ctx, response.body);
    }
    // Change to a different login state
    else if (response.success && response.body && response.body.state && VALID_USER_AUTH_STATES.includes(response.body.state)) {
      ctx.patchState({
        state: response.body.state,
        stateData: response.body,
      });
    }
    // Show error message for unverified email address
    else if (!response.success && response.body && response.body.unverifiedEmail) {
      this.messageService.error("Unable to login without a verified email address. Please verify your email address before continuing.");
    }
    // Show error message (TwoFactor)
    else if (!response.success && state === USER_AUTH_STATES.twoFactorAuth) {
      this.messageService.error(Array.isArray(response.errors) ? response.errors[0] : "Invalid Access Code");
    }
    // Show error message (Unauthenticated)
    else if (!response.success && state === USER_AUTH_STATES.unauthenticated) {
      this.messageService.error(Array.isArray(response.errors) ? response.errors[0] : "Invalid Username or Password");
    }
  }

  private _setUser(ctx: StateContext<SystemStateModel>, user: any): void {
    const currentUser = ctx.getState().user;
    if (currentUser && currentUser._id === user._id && currentUser.role === user.role) {
      return;
    }

    if (user && user._id) {
      this.websocketProvider.emit("auth/login", user);
      ctx.dispatch(new GetServerSettingsAction());
      ctx.patchState({
        user,
        state: USER_AUTH_STATES.authenticated,
        stateData: null,
      });
    } else {
      ctx.patchState({
        user: null,
        state: USER_AUTH_STATES.unauthenticated,
        stateData: null,
      });
    }
  }
}

